import React, { useState } from 'react';
import { 
  placeHelpers, 
  interestHelpers, 
  friendshipHelpers, 
  meetupHelpers, 
  messageHelpers, 
  notificationHelpers,
  supabaseHelpers 
} from '../config/supabase';

const DatabaseTest = ({ user }) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [testPlace, setTestPlace] = useState(null);
  const [toast, setToast] = useState(null);

  const addResult = (test, status, message, data = null) => {
    setResults(prev => [...prev, { test, status, message, data, timestamp: new Date() }]);
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000); // Auto-hide after 3 seconds
  };

  const runAllTests = async () => {
    setLoading(true);
    setResults([]);

    try {
      // Test 1: Create/Get Place
      addResult('Starting Tests', 'info', 'Beginning database test suite...');
      
      const { data: place, error: placeError } = await placeHelpers.getOrCreatePlace('ChIJN1t_tDeuEmsRUsoyG83frY4');
      if (placeError) {
        addResult('Create Place', 'error', `Failed to create place: ${placeError.message}`, placeError);
        addResult('Remaining Tests', 'skipped', 'Skipping remaining tests due to place creation failure');
        setLoading(false);
        return;
      } else {
        setTestPlace(place);
        addResult('Create Place', 'success', `Place created/found: ${place.id}`, place);
      }

      if (!place?.id) {
        addResult('Place Validation', 'error', 'Place was created but has no ID');
        addResult('Remaining Tests', 'skipped', 'Skipping remaining tests due to invalid place data');
        setLoading(false);
        return;
      }

      if (!user?.sub) {
        addResult('User Validation', 'error', 'No user.sub found - cannot continue with user-specific tests');
        setLoading(false);
        return;
      }

      // Test 2: Show Interest
      const { data: interest, error: interestError } = await interestHelpers.showInterest(
        user.sub, 
        place.id, 
        60, 
        'Testing interest feature'
      );
      if (interestError) {
        addResult('Show Interest', 'error', interestError.message);
      } else {
        addResult('Show Interest', 'success', 'Interest shown successfully', interest);
      }

      // Test 3: Toggle Favorite
      const { data: favorite, error: favError } = await interestHelpers.toggleFavorite(
        user.sub, 
        place.id, 
        true
      );
      if (favError) {
        addResult('Toggle Favorite', 'error', favError.message);
      } else {
        addResult('Toggle Favorite', 'success', 'Place marked as favorite', favorite);
      }

      // Test 4: Get Active Interests
      const { data: activeInterests, error: activeError } = await interestHelpers.getActiveInterests(user.sub);
      if (activeError) {
        addResult('Get Active Interests', 'error', activeError.message);
      } else {
        addResult('Get Active Interests', 'success', `Found ${activeInterests.length} active interests`, activeInterests);
      }

      // Test 5: Get Favorites
      const { data: favorites, error: favListError } = await interestHelpers.getUserFavorites(user.sub);
      if (favListError) {
        addResult('Get Favorites', 'error', favListError.message);
      } else {
        addResult('Get Favorites', 'success', `Found ${favorites.length} favorites`, favorites);
      }

      // Test 6: Create Meetup
      const { data: meetup, error: meetupError } = await meetupHelpers.createMeetup(
        user.sub,
        place.id,
        'Test Meetup',
        'This is a test meetup'
      );
      if (meetupError) {
        addResult('Create Meetup', 'error', meetupError.message);
      } else {
        addResult('Create Meetup', 'success', 'Meetup created successfully', meetup);
      }

      // Test 7: Create Notification
      const { data: notification, error: notifError } = await notificationHelpers.createNotification(
        user.sub,
        'test',
        'Test Notification',
        'This is a test notification',
        meetup?.id
      );
      if (notifError) {
        addResult('Create Notification', 'error', notifError.message);
      } else {
        addResult('Create Notification', 'success', 'Notification created', notification);
      }

      // Test 8: Get User Notifications
      const { data: notifications, error: notifListError } = await notificationHelpers.getUserNotifications(user.sub);
      if (notifListError) {
        addResult('Get Notifications', 'error', notifListError.message);
      } else {
        addResult('Get Notifications', 'success', `Found ${notifications.length} notifications`, notifications);
      }

      // Test 9: Test Logging
      await supabaseHelpers.logEvent('database_test_completed', {
        user_id: user.sub,
        test_count: 8,
        completed_at: new Date().toISOString()
      }, user.sub, 'test', 'database_test');
      addResult('Event Logging', 'success', 'Test event logged successfully');

      // Test 10: Get Recent Logs
      const { data: logs, error: logsError } = await supabaseHelpers.getLogs(10);
      if (logsError) {
        addResult('Get Logs', 'error', logsError.message);
      } else {
        addResult('Get Logs', 'success', `Retrieved ${logs.length} recent logs`, logs);
      }

    } catch (error) {
      addResult('General Error', 'error', error.message);
    }

    setLoading(false);
  };

  const testCleanup = async () => {
    setLoading(true);
    const { data, error } = await interestHelpers.cleanupExpiredInterests();
    
    if (error) {
      addResult('Cleanup Test', 'error', error.message);
    } else {
      addResult('Cleanup Test', 'success', `Cleaned up ${data?.length || 0} expired interests`, data);
    }
    setLoading(false);
  };

  const testFriendship = async () => {
    if (!user?.sub) {
      addResult('Friendship Test', 'error', 'No user logged in');
      return;
    }

    setLoading(true);
    
    // Test sending friend request to a dummy user ID (use timestamp to make it unique)
    const dummyFriendId = `test_friend_${Date.now()}`;
    const { data, error } = await friendshipHelpers.sendFriendRequest(user.sub, dummyFriendId);
    
    if (error) {
      if (error.message && error.message.includes('duplicate key')) {
        addResult('Send Friend Request', 'warning', 'Duplicate friendship already exists (this is expected if you\'ve run this test before)', error);
      } else {
        addResult('Send Friend Request', 'error', error.message);
      }
    } else {
      addResult('Send Friend Request', 'success', 'Friend request sent', data);
    }

    // Test getting friends list
    const { data: friends, error: friendsError } = await friendshipHelpers.getFriends(user.sub);
    if (friendsError) {
      addResult('Get Friends', 'error', friendsError.message);
    } else {
      addResult('Get Friends', 'success', `Found ${friends.length} friends`, friends);
    }

    setLoading(false);
  };

  const exportToClipboard = async () => {
    const exportData = {
      timestamp: new Date().toISOString(),
      user: {
        email: user?.email || 'Not logged in',
        id: user?.sub || 'N/A'
      },
      testPlace: testPlace?.google_place_id || 'Not created yet',
      totalTests: results.length,
      results: results.map(result => ({
        test: result.test,
        status: result.status,
        message: result.message,
        timestamp: result.timestamp.toISOString(),
        data: result.data
      }))
    };

    try {
      await navigator.clipboard.writeText(JSON.stringify(exportData, null, 2));
      showToast('✅ Test results copied to clipboard! Ready to paste and share.', 'success');
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      // Fallback: create a temporary textarea
      const textarea = document.createElement('textarea');
      textarea.value = JSON.stringify(exportData, null, 2);
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      showToast('✅ Test results copied to clipboard (fallback method)', 'success');
    }
  };

  return (
    <div style={{ 
      padding: '20px', 
      margin: '20px', 
      border: '1px solid #ccc', 
      borderRadius: '8px',
      backgroundColor: '#f9f9f9',
      maxWidth: '800px',
      position: 'relative'
    }}>
      {/* Toast Notification */}
      {toast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 1000,
          padding: '12px 20px',
          borderRadius: '8px',
          color: 'white',
          fontSize: '14px',
          fontWeight: '500',
          backgroundColor: toast.type === 'success' ? '#28a745' : 
                          toast.type === 'error' ? '#dc3545' : '#17a2b8',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          animation: 'slideInRight 0.3s ease-out',
          maxWidth: '300px'
        }}>
          {toast.message}
        </div>
      )}

      <style>
        {`
          @keyframes slideInRight {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}
      </style>

      <h3>Complete Database Test Suite</h3>
      
      <div style={{ marginBottom: '20px' }}>
        <strong>Current User:</strong> {user?.email || 'Not logged in'}<br/>
        <strong>User ID:</strong> {user?.sub || 'N/A'}<br/>
        <strong>Test Place:</strong> {testPlace?.google_place_id || 'Not created yet'}
      </div>

      <div style={{ marginBottom: '20px' }}>
        <button 
          onClick={runAllTests} 
          disabled={loading}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            marginRight: '10px'
          }}
        >
          {loading ? 'Running Tests...' : 'Run Full Test Suite'}
        </button>

        <button 
          onClick={testCleanup} 
          disabled={loading}
          style={{
            padding: '10px 20px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            marginRight: '10px'
          }}
        >
          Test Cleanup
        </button>

        <button 
          onClick={testFriendship} 
          disabled={loading}
          style={{
            padding: '10px 20px',
            backgroundColor: '#ffc107',
            color: 'black',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            marginRight: '10px'
          }}
        >
          Test Friendships
        </button>

        {results.length > 0 && (
          <button 
            onClick={exportToClipboard}
            style={{
              padding: '10px 20px',
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            📋 Export JSON
          </button>
        )}
      </div>

      {results.length > 0 && (
        <div>
          <h4>Test Results:</h4>
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {results.map((result, index) => (
              <div 
                key={index}
                style={{
                  padding: '12px',
                  margin: '8px 0',
                  borderRadius: '6px',
                  backgroundColor: 
                    result.status === 'success' ? '#d4edda' : 
                    result.status === 'error' ? '#f8d7da' : 
                    '#fff3cd',
                  border: `1px solid ${
                    result.status === 'success' ? '#c3e6cb' : 
                    result.status === 'error' ? '#f5c6cb' : 
                    '#ffeaa7'
                  }`
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong>{result.test}:</strong>
                  <small>{result.timestamp.toLocaleTimeString()}</small>
                </div>
                <div style={{ marginTop: '4px' }}>{result.message}</div>
                {result.data && (
                  <details style={{ marginTop: '8px' }}>
                    <summary style={{ cursor: 'pointer', fontSize: '12px', color: '#666' }}>
                      View Data
                    </summary>
                    <pre style={{ 
                      fontSize: '11px', 
                      backgroundColor: '#f8f9fa', 
                      padding: '8px', 
                      borderRadius: '4px',
                      overflow: 'auto',
                      maxHeight: '200px'
                    }}>
                      {JSON.stringify(result.data, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DatabaseTest; 